import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { createVendor } from '../../api/vendorsApi.js';
import { createUser } from '../../api/usersApi.js';
import { listRoles } from '../../api/rolesApi.js';
import { useTenantIdentityState } from '../../shell/useTenantIdentity.js';

import {
  GSTIN_PATTERN as GSTIN_RE,
  PAN_PATTERN as PAN_RE,
} from '../../../../shared/contracts/backend-enums.js';
const TAN_RE = /^[A-Z]{4}[0-9]{5}[A-Z]$/;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const LOGIN_SUFFIX = /^[A-Za-z0-9_]{2,40}$/;

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function VendorCreatePage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { identity: tenant, error: tenantError } = useTenantIdentityState(token);
  const [vendorName, setVendorName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [tanNumber, setTanNumber] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [gstCertDocId, setGstCertDocId] = useState('');
  const [panCardDocId, setPanCardDocId] = useState('');
  const [chequeDocId, setChequeDocId] = useState('');
  const [loginSuffix, setLoginSuffix] = useState('');
  const [password, setPassword] = useState('');
  const [vendorRoleId, setVendorRoleId] = useState<string | null>(null);
  // The login needs the tenant's vendor role; without it nothing may be created.
  const [rolesRead, setRolesRead] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A login id without the tenant prefix must never be created, so Save
  // waits for the tenant to be known.
  const prefix = tenant?.tenantCode ? `${tenant.tenantCode}_` : '';
  const canSave = !busy && !!prefix && !!vendorRoleId;

  useEffect(() => {
    let alive = true;
    void listRoles(token)
      .then((rs) => {
        if (!alive) return;
        setVendorRoleId(rs.find((r) => r.roleCategoryCode === 'VENDOR')?.roleId ?? null);
        setRolesRead(true);
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  async function submit() {
    if (!prefix || !vendorRoleId) return;
    if (!vendorName) { setError(t('vendors.create.err.vendorName')); return; }
    if (!legalName) { setError(t('vendors.create.err.legalName')); return; }
    if (!contactPerson.trim()) { setError(t('vendors.create.err.contactPerson')); return; }
    if (!/^[6-9][0-9]{9}$/.test(mobileNumber)) { setError(t('vendors.create.err.mobile')); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setError(t('vendors.create.err.email')); return; }
    if (!address.trim()) { setError(t('vendors.create.err.address')); return; }
    if (!GSTIN_RE.test(gstNumber)) { setError(t('vendors.create.err.gstin')); return; }
    if (!PAN_RE.test(panNumber)) { setError(t('vendors.create.err.pan')); return; }
    if (tanNumber.trim() && !TAN_RE.test(tanNumber.trim())) { setError(t('vendors.create.err.tan')); return; }
    if (!/^[0-9]{9,18}$/.test(bankAccountNumber.trim())) { setError(t('vendors.create.err.bankAccount')); return; }
    if (!IFSC_RE.test(ifscCode.trim())) { setError(t('vendors.create.err.ifsc')); return; }
    if (!gstCertDocId || !panCardDocId || !chequeDocId) {
      setError(t('vendors.create.err.kyc'));
      return;
    }
    if (!LOGIN_SUFFIX.test(loginSuffix.trim())) { setError(t('users.create.err.loginId')); return; }
    if (password.length < 8) { setError(t('users.create.err.password')); return; }

    setError(null); setBusy(true);
    try {
      const vendor = await createVendor(
        {
          vendorName: vendorName.trim(),
          legalName: legalName.trim(),
          contactPerson: contactPerson.trim(),
          mobileNumber: mobileNumber.trim(),
          email: email.trim(),
          address: address.trim(),
          gstNumber: gstNumber.trim(),
          panNumber: panNumber.trim(),
          bankAccountNumber: bankAccountNumber.trim(),
          ifscCode: ifscCode.trim(),
          gstCertDocRef: gstCertDocId.trim(),
          panCardDocRef: panCardDocId.trim(),
          cancelledChequeDocRef: chequeDocId.trim(),
          ...(tanNumber.trim() ? { tanNumber: tanNumber.trim() } : {}),
        },
        token,
      );

      // The firm and its login are one action for the operator, but two calls:
      // the vendor endpoint does not issue logins.
      const [first, ...rest] = contactPerson.trim().split(/\s+/);
      await createUser(
        {
          employeeCode: `${prefix}${loginSuffix.trim()}`,
          firstName: first ?? vendorName.trim(),
          lastName: rest.join(' ') || vendorName.trim(),
          mobileNumber: `+91${mobileNumber.trim()}`,
          emailAddress: email.trim(),
          temporaryPassword: password,
          effectiveFromDate: today(),
          userType: 'VENDOR',
          vendorId: vendor.vendorId,
          defaultRoleId: vendorRoleId,
        },
        token,
      );

      navigate(`/admin/vendors/${vendor.vendorId}`);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <main aria-label="vendor-new">
      <h1>{t('vendors.create.title')}</h1>
      {tenantError ? <p role="alert" aria-label="tenant-error">{tenantError}</p> : null}
      {rolesRead && !vendorRoleId ? (
        <p role="alert" aria-label="role-missing">{t('vendors.create.err.noVendorRole')}</p>
      ) : null}
      {error ? <p role="alert">{error}</p> : null}

      <label htmlFor="vname">{t('vendors.create.vendorName')}</label>
      <input id="vname" aria-label="vendor-name" value={vendorName} onChange={(e) => setVendorName(e.target.value)} />

      <label htmlFor="lname">{t('vendors.create.legalName')}</label>
      <input id="lname" aria-label="legal-name" value={legalName} onChange={(e) => setLegalName(e.target.value)} />

      <label htmlFor="cperson">{t('vendors.create.contactPerson')}</label>
      <input id="cperson" aria-label="contact-person" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} />

      <label htmlFor="mob">{t('vendors.create.mobile')}</label>
      <input id="mob" aria-label="mobile" value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)} />

      <label htmlFor="em">{t('vendors.create.email')}</label>
      <input id="em" aria-label="email" value={email} onChange={(e) => setEmail(e.target.value)} />

      <label htmlFor="addr">{t('vendors.create.address')}</label>
      <input id="addr" aria-label="address" value={address} onChange={(e) => setAddress(e.target.value)} />

      <label htmlFor="gst">{t('vendors.create.gstin')}</label>
      <input id="gst" aria-label="gstin" value={gstNumber} onChange={(e) => setGstNumber(e.target.value.toUpperCase())} />

      <label htmlFor="pan">{t('vendors.create.pan')}</label>
      <input id="pan" aria-label="pan" value={panNumber} onChange={(e) => setPanNumber(e.target.value.toUpperCase())} />

      <label htmlFor="tan">{t('vendors.create.tan')}</label>
      <input id="tan" aria-label="tan" value={tanNumber} onChange={(e) => setTanNumber(e.target.value.toUpperCase())} />

      <fieldset aria-label="bank-details">
        <legend>{t('vendors.create.secBank')}</legend>
        <label htmlFor="bacc">{t('vendors.create.bankAccount')}</label>
        <input id="bacc" aria-label="bank-account" value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value.replace(/\D/g, ''))} />
        <label htmlFor="ifsc">{t('vendors.create.ifsc')}</label>
        <input id="ifsc" aria-label="ifsc" value={ifscCode} onChange={(e) => setIfscCode(e.target.value.toUpperCase())} />
      </fieldset>

      <fieldset aria-label="kyc-docs">
        <legend>{t('vendors.create.kycLegend')}</legend>
        <label htmlFor="gstc">{t('vendors.create.gstCert')}</label>
        <input id="gstc" aria-label="gst-cert-doc-id" value={gstCertDocId} onChange={(e) => setGstCertDocId(e.target.value)} />
        <label htmlFor="panc">{t('vendors.create.panCard')}</label>
        <input id="panc" aria-label="pan-card-doc-id" value={panCardDocId} onChange={(e) => setPanCardDocId(e.target.value)} />
        <label htmlFor="chq">{t('vendors.create.cheque')}</label>
        <input id="chq" aria-label="cheque-doc-id" value={chequeDocId} onChange={(e) => setChequeDocId(e.target.value)} />
      </fieldset>

      <fieldset aria-label="vendor-login">
        <legend>{t('users.create.loginSection')}</legend>
        <label htmlFor="vlogin">{t('users.create.loginId')}</label>
        <span>
          <span aria-label="login-id-prefix"><strong>{prefix}</strong></span>
          <input id="vlogin" aria-label="login-suffix" value={loginSuffix} onChange={(e) => setLoginSuffix(e.target.value)} />
        </span>
        {loginSuffix ? (
          <p data-testid="vendor-login-preview">
            {t('users.create.loginIdPreview')}: <strong>{prefix}{loginSuffix}</strong>
          </p>
        ) : null}
        <label htmlFor="vpass">{t('users.create.tempPassword')}</label>
        <input id="vpass" type="password" aria-label="temp-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <p>{t('users.create.passwordRules')}</p>
      </fieldset>

      <button onClick={submit} disabled={!canSave} aria-label="create-vendor">{busy ? t('vendors.create.saving') : t('vendors.create.submit')}</button>
    </main>
  );
}
